import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

export default function AnimationViewer() {
  const navigate = useNavigate();
  const location = useLocation();
  const item = location?.state?.item || location?.state?.game || null;
  const [playing, setPlaying] = useState(false);
  const [loadError, setLoadError] = useState(false);

  if (loadError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-8" style={{ background: 'var(--color-background)' }}>
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.1)' }}>
          <Icon name="AlertCircle" size={32} color="var(--color-error)" />
        </div>
        <div className="text-center">
          <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Content could not be loaded.</h2>
          <p className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>The animation failed to load. Please try again.</p>
        </div>
        <div className="flex flex-wrap gap-3 justify-center">
          <button onClick={() => setLoadError(false)} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold" style={{ background: 'var(--color-primary)', color: '#fff' }}>
            <Icon name="RefreshCw" size={15} color="#fff" /> Retry
          </button>
          <button onClick={() => navigate('/creator-dashboard')} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold" style={{ background: 'var(--color-card)', color: 'var(--color-foreground)', border: '1px solid var(--color-border)' }}>
            <Icon name="LayoutDashboard" size={15} /> Back to Dashboard
          </button>
          <button onClick={() => navigate('/creator-dashboard')} className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold" style={{ background: 'var(--color-card)', color: 'var(--color-foreground)', border: '1px solid var(--color-border)' }}>
            <Icon name="Library" size={15} /> Back to Library
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--color-background)' }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b" style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}>
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:bg-[var(--color-muted)]"
          aria-label="Go back"
        >
          <Icon name="ArrowLeft" size={18} color="var(--color-foreground)" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold truncate" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
            {item?.title || 'Animation Viewer'}
          </h1>
          <p className="text-xs" style={{ color: 'var(--color-muted-foreground)' }}>Animation</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate('/creator-dashboard')}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all hover:bg-[var(--color-muted)]"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}
          >
            <Icon name="LayoutDashboard" size={13} />
            Dashboard
          </button>
          <button
            onClick={() => navigate('/creator-dashboard')}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border transition-all hover:bg-[var(--color-muted)]"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}
          >
            <Icon name="Library" size={13} />
            Library
          </button>
        </div>
      </div>
      {/* Animation Player */}
      <div className="flex-1 flex flex-col items-center justify-start p-4 md:p-8 gap-6 max-w-4xl mx-auto w-full">
        <div
          className="w-full rounded-2xl overflow-hidden relative"
          style={{ background: 'linear-gradient(135deg, #1e1b4b, #312e81)', aspectRatio: '16/9', border: '1px solid var(--color-border)' }}
        >
          {item?.thumbnail ? (
            <>
              <Image
                src={item?.thumbnail}
                alt={item?.thumbnailAlt || item?.title || 'Animation preview'}
                className="w-full h-full object-cover"
                style={{ opacity: playing ? 0.5 : 1, transition: 'opacity 0.3s' }}
              />
              {playing && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex gap-1.5 items-end">
                    {[1, 2, 3, 4, 5]?.map((i) => (
                      <div
                        key={i}
                        className="w-2 rounded-full"
                        style={{
                          background: 'var(--color-accent)',
                          height: `${20 + i * 8}px`,
                          opacity: 0.8,
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                <button
                  onClick={() => setPlaying((v) => !v)}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm shadow-lg transition-transform hover:scale-105"
                  style={{ background: playing ? 'rgba(255,255,255,0.2)' : 'var(--color-accent)', color: '#fff' }}
                >
                  <Icon name={playing ? 'Pause' : 'Play'} size={16} color="#fff" />
                  {playing ? 'Pause' : 'Play Animation'}
                </button>
              </div>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center">
                <Icon name="Film" size={48} color="rgba(255,255,255,0.3)" />
                <p className="text-white/50 text-sm mt-2">No preview available</p>
              </div>
            </div>
          )}
        </div>

        {/* Info */}
        {item && (
          <div className="w-full rounded-xl p-4" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}>
            <h2 className="text-lg font-bold mb-1" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{item?.title}</h2>
            {item?.prompt && <p className="text-sm" style={{ color: 'var(--color-muted-foreground)' }}>{item?.prompt}</p>}
            {item?.createdAt && <p className="text-xs mt-2" style={{ color: 'var(--color-muted-foreground)' }}>Created: {item?.createdAt}</p>}
          </div>
        )}

        {/* Action Buttons */}
        <div className="w-full grid grid-cols-2 sm:grid-cols-3 gap-3">
          <button
            onClick={() => {
              const link = document.createElement('a');
              link.href = item?.thumbnail || '#';
              link.download = `${item?.title || 'animation'}.gif`;
              link?.click();
            }}
            className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:opacity-80"
            style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
          >
            <Icon name="Download" size={20} color="var(--color-primary)" />
            <span className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>Download</span>
          </button>
          <button
            onClick={() => navigate('/creator-dashboard', { state: { remixItem: item } })}
            className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:opacity-80"
            style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
          >
            <Icon name="Shuffle" size={20} color="var(--color-accent)" />
            <span className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>Remix Animation</span>
          </button>
          <button
            onClick={() => navigate('/creator-dashboard')}
            className="flex flex-col items-center gap-2 p-4 rounded-xl transition-all hover:opacity-80"
            style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
          >
            <Icon name="Library" size={20} color="var(--color-muted-foreground)" />
            <span className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>Library</span>
          </button>
        </div>
      </div>
    </div>
  );
}
